import {define, inject} from '@injex/core';
import {makeObservable, observable} from 'mobx';
import IChart from '../../interfaces/IChart';
import {ColumnsManager} from '@vidazoo/ui-kit';
import ApiManager from '../../../../common/api/ApiManager.mdl';
import {DataListQuery} from '../../../../common/models/DataListQuery';
import {ChartsTableColumns} from '../common/constants';
import UserManager from '../../../../common/managers/UserManager.mdl';
import {AlertsManager} from '../../../../common/alerts/managers/alertsManager.mdl';
import {AlertAccent} from '../../../../common/alerts/common/enums';

export type ChartBulkAction = 'delete' | 'activate' | 'deactivate';

@define()
export class ChartsListModel extends ColumnsManager<IChart> {
    @inject() private apiManager: ApiManager;
    @inject() private userManager: UserManager;
    @inject() private alertsManager: AlertsManager;
    public items: DataListQuery<IChart, []>;
    @observable public pendingIds: string[] = [];
    @observable public actionError = '';

    constructor() {
        super({persistKey: 'charts_list_classic_columns'});
        this.setColumns(ChartsTableColumns);
        this.items = new DataListQuery(this._fetchItems.bind(this), {defaultValue: [], dataObservType: observable.ref});
        makeObservable(this);
    }

    public get data(): IChart[] { return this.items.data || []; }
    public get isLoading(): boolean { return this.items.isLoading; }

    private async _fetchItems() {
        const response = await this.apiManager.charts.getAll({page: 1, pageSize: 100,
            fields: {name: 1, type: 1, created: 1, updated: 1, isActive: 1}, filter: {user: this.userManager._id}}, false);
        return response.results;
    }

    public fetch() { return this.items.invalidate().fetch(); }

    public toggleActive = async (item: IChart) => {
        if (this.pendingIds.includes(item._id)) return;
        await this.applyAction('activate', [item._id], !item.isActive);
    };

    public confirmDelete(item: IChart) {
        this.alertsManager.setConfirmAlert({content: `Delete Chart? Are you sure you want to delete ${item.name}?`,
            confirmText: 'Yes, Delete', cancelText: 'No, Cancel', onConfirm: () => this.applyAction('delete', [item._id])}, AlertAccent.Error);
    }

    public confirmBulkAction(action: ChartBulkAction, ids: string[], onComplete: (ids: string[]) => void) {
        const selected = [...ids];
        if (!selected.length || selected.some(id => this.pendingIds.includes(id))) return;
        this.alertsManager.setConfirmAlert({content: `Are you sure you want to ${action} ${selected.length} Charts?`,
            confirmText: 'Confirm', cancelText: 'Cancel', onConfirm: async () => {
                const completed = await this.applyAction(action, selected);
                onComplete(completed);
            }}, action === 'delete' ? AlertAccent.Error : AlertAccent.Warning);
    }

    private async applyAction(action: ChartBulkAction, ids: string[], active = action === 'activate'): Promise<string[]> {
        this.actionError = '';
        this.pendingIds = [...this.pendingIds, ...ids];
        const results = await Promise.allSettled(ids.map(id => action === 'delete'
            ? this.apiManager.charts.deleteById(id) : this.apiManager.charts.activeById(id, active)));
        const completed = ids.filter((_, i) => results[i].status === 'fulfilled');
        this.items.data = action === 'delete' ? this.data.filter(item => !completed.includes(item._id))
            : this.data.map(item => completed.includes(item._id) ? {...item, isActive: active} : item);
        this.pendingIds = this.pendingIds.filter(id => !ids.includes(id));
        if (completed.length !== ids.length) this.actionError = 'Some charts could not be updated. Please try again.';
        return completed;
    }
}
